import { Icon } from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { mdiAlertCircleOutline } from "@mdi/js";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  Container,
  Grid,
  OutlinedInput,
  Paper,
  Tab,
  Tooltip,
  Typography,
} from "@mui/material";
import { createFileRoute, Navigate } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import {
  type ChangeEvent,
  type FC,
  type SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import type { AuthContextData } from "../../../../../@types/contexts/Auth/AuthContext";
import type { ServiceQuotation } from "../../../../../@types/services/ServiceQuotation";
import { listServiceNegotiations } from "../../../../../__generated__/orval/endpoints/service-negotiations/service-negotiations";
import {
  showServiceQuotation,
  updateServiceQuotation,
} from "../../../../../__generated__/orval/endpoints/service-quotations/service-quotations";
import type {
  EntitiesServiceNegotiationListResponseOutput,
  EntitiesServiceQuotationDetailOutput,
  ListServiceNegotiationsParams,
} from "../../../../../__generated__/orval/models";
import { AssignToService } from "../../../../../components/molecules/AssignToService";
import { ListAttachmentServiceQuotation } from "../../../../../components/molecules/ListAttachmentServiceQuotation";
import { LoadingCentral } from "../../../../../components/molecules/LoadingCentral";
import { MaintenanceOrder } from "../../../../../components/molecules/MaintenanceOrder";
import { ProgressBar } from "../../../../../components/molecules/ProgressBar";
import { ServiceBillingInformation } from "../../../../../components/molecules/ServiceBillingInformation";
import { ServiceNegotiationAudits } from "../../../../../components/molecules/ServiceNegotiationAudits";
import { ServiceNegotiationChats } from "../../../../../components/molecules/ServiceNegotiationChats";
import { ServiceNegotiationTable } from "../../../../../components/molecules/ServiceNegotiationTable";
import { ServiceQuotationOfferDeadline } from "../../../../../components/molecules/ServiceQuotationOfferDeadline";
import { ServiceTable } from "../../../../../components/molecules/ServiceTable";
import { Actions } from "../../../../../components/organisms/Actions";
import { ForbiddenError } from "../../../../../errors/ForbiddenError";
import { useAuth } from "../../../../../hooks/useAuth";
import { i18nextParseToDate } from "../../../../../utils/i18nextParseTo";
import { ProposalEqualization } from "./-internal_components/ProposalEqualization";

type ServiceNegotiation = NonNullable<
  EntitiesServiceNegotiationListResponseOutput["results"]
>[number];

interface RouteContext {
  auth: AuthContextData;
}

const VENDOR_DESCRIPTION_MAX_LENGTH = 5000;

const TAB_VALUES = {
  attachments: "2",
  audits: "6",
  internalChat: "3",
  maintenanceOrder: "4",
  moreInformation: "5",
  negotiations: "0",
  services: "1",
} as const;

const PROPOSAL_EQUALIZATION_AVAILABLE_STATUS_NAMES = new Set([
  "Bid Winner Chosen",
  "Checking Proposal",
  "Checking Lines Changes",
  "Choosing Winner",
  "Waiting Approval",
  "Waiting Change of Details",
  "Renegotiation",
  "Renegotiable",
  "Issuing Purchase Order",
  "Purchase Order Created",
  "Finished",
]);

const searchSchema = z.object({
  service_negotiation_id: z.optional(z.coerce.number()).catch(undefined),
  tab: z
    .optional(z.enum(["chat", "internal_chat", "negotiations"]))
    .catch(undefined),
});

const toLegacyServiceQuotation = (
  resource: EntitiesServiceQuotationDetailOutput,
): ServiceQuotation => {
  return resource as unknown as ServiceQuotation;
};

const ShowServiceQuotation: FC = () => {
  const { serviceQuotationId } = Route.useParams();
  const navigate = Route.useNavigate();
  const {
    chat_notification_id: chatNotificationId,
    service_negotiation_id: selectedServiceNegotiationId,
    tab,
  } = Route.useSearch();

  const { user } = useAuth();
  const { t } = useTranslation();
  const chatSectionRef = useRef<HTMLDivElement>(null);
  const [scrollToLatestRequest, setScrollToLatestRequest] = useState(0);
  const focusedNotificationRequest = useRef(0);

  useEffect(() => {
    if (chatNotificationId) {
      setScrollToLatestRequest((request) => request + 1);
      setValue(TAB_VALUES.internalChat);
    }
  }, [chatNotificationId]);

  const [serviceQuotation, setServiceQuotation] = useState<ServiceQuotation>();
  const [serviceNegotiations, setServiceNegotiations] =
    useState<ServiceNegotiation[]>();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [value, setValue] = useState<string>(
    tab === "chat" || tab === "internal_chat" || selectedServiceNegotiationId
      ? TAB_VALUES.internalChat
      : TAB_VALUES.negotiations,
  );
  const [descriptionForVendor, setDescriptionForVendor] = useState("");
  const [savingDescriptionForVendor, setSavingDescriptionForVendor] =
    useState(false);
  const [expandedDescriptionForVendor, setExpandedDescriptionForVendor] =
    useState(false);
  const [scopeOfWorkFocusTrigger, setScopeOfWorkFocusTrigger] = useState(0);
  const [actionsRefreshTrigger, setActionsRefreshTrigger] = useState(0);

  const handleFetchServiceQuotation = useCallback(
    async (id: number, options?: { showLoading?: boolean }): Promise<void> => {
      const showLoading = options?.showLoading ?? true;

      if (showLoading) {
        setLoading(() => true);
      }

      await showServiceQuotation(id)
        .then((resource) => {
          const mappedResource = toLegacyServiceQuotation(resource);
          setServiceQuotation(() => mappedResource);
        })
        .catch(() => {
          setNotFound(() => true);
        })
        .finally(() => {
          if (showLoading) {
            setLoading(() => false);
          }
        });
    },
    [],
  );

  const handleFetchServiceNegotiations = useCallback(
    async (filters?: ListServiceNegotiationsParams) => {
      setLoading(() => true);
      const params: ListServiceNegotiationsParams = {
        identifier: filters?.identifier,
        items_per_page: 25,
        page: 1,
        pr_number: filters?.pr_number,
        service_quotation_id: filters?.service_quotation_id,
        status_name: filters?.status_name,
      };

      await listServiceNegotiations(params)
        .then((sq) => {
          if (sq.results) {
            setServiceNegotiations(sq.results);
          }
        })
        .catch(() => {
          setNotFound(() => true);
        })
        .finally(() => {
          setLoading(() => false);
        });
    },
    [],
  );

  useEffect(() => {
    if (!serviceQuotationId) {
      setNotFound(() => true);
      return;
    }

    handleFetchServiceQuotation(parseInt(serviceQuotationId, 10));
    handleFetchServiceNegotiations({
      service_quotation_id: parseInt(serviceQuotationId, 10),
    });
  }, [
    serviceQuotationId,
    handleFetchServiceNegotiations,
    handleFetchServiceQuotation,
  ]);

  useEffect(() => {
    setDescriptionForVendor(serviceQuotation?.description_for_vendor || "");
  }, [serviceQuotation?.description_for_vendor]);

  useEffect(() => {
    if (
      tab === "chat" ||
      tab === "internal_chat" ||
      selectedServiceNegotiationId
    ) {
      setValue(TAB_VALUES.internalChat);
      return;
    }

    if (tab === "negotiations") {
      setValue(TAB_VALUES.negotiations);
    }
  }, [selectedServiceNegotiationId, tab]);

  useEffect(() => {
    if (
      scrollToLatestRequest === focusedNotificationRequest.current ||
      loading ||
      value !== TAB_VALUES.internalChat ||
      !serviceQuotation?.chat ||
      (selectedServiceNegotiationId && !serviceNegotiations?.length)
    ) {
      return;
    }

    const frame = requestAnimationFrame(() => {
      if (!chatSectionRef.current) return;
      chatSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
      chatSectionRef.current?.focus({ preventScroll: true });
      focusedNotificationRequest.current = scrollToLatestRequest;
    });

    return () => cancelAnimationFrame(frame);
  }, [
    loading,
    scrollToLatestRequest,
    selectedServiceNegotiationId,
    serviceNegotiations,
    serviceQuotation?.chat,
    value,
  ]);

  const canManageServiceQuotation =
    !!user && serviceQuotation?.assigned_to?.id === user.id;

  const canEditVendorDescription =
    canManageServiceQuotation &&
    !!user &&
    Array.isArray(user.roles_names) &&
    user.roles_names.includes("Service Quotation - Update");

  const handleSaveDescriptionForVendor = useCallback(async () => {
    if (!serviceQuotation) {
      return;
    }

    setSavingDescriptionForVendor(true);

    try {
      const resource = await updateServiceQuotation(serviceQuotation.id, {
        description_for_vendor: descriptionForVendor,
      });

      if (resource) {
        setServiceQuotation(toLegacyServiceQuotation(resource));
        enqueueSnackbar(t("serviceQuotation.vendorDescriptionSaved"), {
          variant: "success",
        });
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar(t("serviceQuotation.vendorDescriptionSaveError"), {
        variant: "error",
      });
    } finally {
      setSavingDescriptionForVendor(false);
    }
  }, [descriptionForVendor, serviceQuotation, t]);

  if (notFound || !serviceQuotationId) {
    return <Navigate to="/not_found" />;
  }

  const handleChange = (_event: SyntheticEvent, newValue: string): void => {
    setValue(newValue);
  };

  const handleGoToScopeOfWork = (): void => {
    setValue(TAB_VALUES.attachments);
    setScopeOfWorkFocusTrigger((prev) => prev + 1);
  };

  if (loading) {
    return <LoadingCentral />;
  }

  return (
    <Box
      component="main"
      sx={{
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        height: "100%",
        justifyContent: "center",
        px: 2,
        width: "100%",
      }}
    >
      <Container>
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            flexGrow: 1,
            justifyContent: "space-between",
            paddingY: 2,
          }}
        >
          <Box>
            <Typography variant="h5">{t("serviceQuotation.title")}</Typography>
            <Typography variant="h4">
              #{serviceQuotation?.identifier}
            </Typography>
            <Typography
              sx={{
                color: "text.secondary",
              }}
              variant="h6"
            >
              {serviceQuotation?.reference_number}
            </Typography>
          </Box>
          {!!serviceQuotation?.process_instance && (
            <ProgressBar process={serviceQuotation?.process_instance} />
          )}
        </Box>
        <Paper elevation={1}>
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              position: "relative",
              px: 2,
              py: 2,
            }}
          >
            <Typography variant="h5">{t("details")}</Typography>
            <Box
              sx={{
                alignItems: "center",
                display: "flex",
                inset: 0,
                justifyContent: "center",
                pointerEvents: "none",
                position: "absolute",
              }}
            >
              <ServiceQuotationOfferDeadline
                offerSubmissionDeadline={
                  serviceQuotation?.offer_submission_deadline
                }
                statusName={serviceQuotation?.status?.name}
              />
            </Box>
            <ButtonGroup
              aria-label="Small button group"
              size="small"
              sx={{ gap: "5px", ml: "auto" }}
            >
              {!!serviceQuotation && (
                <>
                  {!!serviceQuotation?.purchase_order_item?.purchase_order
                    ?.purchase_order_pdf_document?.attachment && (
                    <Button
                      color="success"
                      disableElevation
                      download={
                        serviceQuotation.purchase_order_item.purchase_order
                          .purchase_order_pdf_document.attachment.filename
                      }
                      href={`/vbuyer/purchase_orders/${serviceQuotation.purchase_order_item.purchase_order.id}/download.pdf`}
                      target="_blank"
                      variant="contained"
                    >
                      Download Purchase Order
                    </Button>
                  )}
                  {canManageServiceQuotation &&
                    user.roles_names.includes("Service Quotation - Update") && (
                      <Button
                        disableElevation
                        onClick={handleGoToScopeOfWork}
                        variant="contained"
                      >
                        {t("serviceNegotiation.scopeOfWork")}
                      </Button>
                    )}
                  <Actions
                    enabled={canManageServiceQuotation}
                    entityId={serviceQuotation.id}
                    offerSubmissionDays={serviceQuotation.offer_submission_days}
                    onActionCompleted={async () => {
                      if (!serviceQuotationId) {
                        return;
                      }

                      const parsedId = parseInt(serviceQuotationId, 10);
                      await Promise.all([
                        handleFetchServiceQuotation(parsedId, {
                          showLoading: false,
                        }),
                        handleFetchServiceNegotiations({
                          service_quotation_id: parsedId,
                        }),
                      ]);
                    }}
                    refreshTrigger={actionsRefreshTrigger}
                    serviceQuotationStatusName={serviceQuotation.status?.name}
                  />
                </>
              )}
            </ButtonGroup>
          </Box>
          <Box sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid size={4}>
                <Typography variant="body2">
                  {t("serviceNegotiation.prNumber")}
                </Typography>
                <Typography variant="subtitle2">
                  {serviceQuotation?.pr_item?.pr_number || ""}
                </Typography>
              </Grid>
              <Grid
                size={4}
                sx={{
                  alignItems: "center",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="body2">
                  {t("serviceNegotiation.itemNumber")}
                </Typography>
                <Typography variant="subtitle2">
                  {serviceQuotation?.pr_item?.item_number || ""}
                </Typography>
              </Grid>
              <Grid
                size={4}
                sx={{
                  alignItems: "flex-end",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="body2">
                  {t("serviceQuotation.requisitionDate")}
                </Typography>
                <Typography variant="subtitle2">
                  {serviceQuotation?.pr_item?.requisition_date
                    ? t(
                        "intlDate",
                        i18nextParseToDate(
                          serviceQuotation.pr_item.requisition_date,
                        ),
                      )
                    : ""}
                </Typography>
              </Grid>
              <Grid size={4}>
                <Typography variant="body2">
                  {t("serviceNegotiation.shortDescription")}
                </Typography>
                <Typography variant="subtitle2">
                  {serviceQuotation?.short_description || ""}
                </Typography>
              </Grid>

              <Grid
                size={4}
                sx={{
                  alignItems: "center",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="body2">
                  {t("serviceQuotation.plant")}
                </Typography>
                <Typography variant="subtitle2">
                  {`${serviceQuotation?.pr_item?.plant?.plant_number} - ${serviceQuotation?.pr_item?.plant?.name}`}
                </Typography>
              </Grid>
              <Grid
                size={4}
                sx={{
                  alignItems: "flex-end",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="body2">
                  {t("serviceNegotiation.purchaseGroup")}
                </Typography>
                <Typography variant="subtitle2">
                  {serviceQuotation?.pr_item?.purchase_group?.name || ""}
                </Typography>
              </Grid>
              <Grid size={4}>
                <Typography variant="body2">
                  {t("serviceQuotation.assignedTo")}
                </Typography>
                <Typography variant="subtitle2">
                  {!!serviceQuotation && (
                    <AssignToService
                      handleFetchServiceQuotation={handleFetchServiceQuotation}
                      service_quotation={serviceQuotation}
                    />
                  )}
                </Typography>
              </Grid>
              <Grid
                size={4}
                sx={{
                  alignItems: "center",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="body2">{t("common.requester")}</Typography>
                <Typography variant="subtitle2">
                  {serviceQuotation?.pr_item?.requester?.full_name || ""}
                </Typography>
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ marginTop: 1 }}>
              <Grid size={12}>
                <Box
                  sx={{
                    alignItems: "center",
                    display: "flex",
                    gap: 1,
                  }}
                >
                  <Typography variant="body2">
                    {t("serviceNegotiation.description")}
                  </Typography>
                  <Tooltip
                    title={t("serviceQuotation.tooltip.descriptionFromSap")}
                  >
                    <Icon
                      fontSize="small"
                      icon={mdiAlertCircleOutline}
                      sx={{
                        opacity: 0.5,
                      }}
                    />
                  </Tooltip>
                </Box>
                <OutlinedInput
                  fullWidth
                  multiline
                  readOnly
                  rows={5}
                  sx={{
                    "&.Mui-disabled": {
                      backgroundColor: "rgba(0, 0, 0, 0.12)",
                      color: "black",
                      opacity: 1,
                      WebkitTextFillColor: "black",
                    },
                    backgroundColor: "white",
                    fontSize: "0.9em",
                    marginTop: "16px",
                  }}
                  value={serviceQuotation?.long_text || ""}
                />
              </Grid>

              {!!serviceQuotation && (
                <Grid size={12}>
                  <Box
                    sx={{
                      alignItems: "center",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="body2">
                      {t("serviceQuotation.descriptionForVendor")}
                    </Typography>
                    <Button
                      onClick={() =>
                        setExpandedDescriptionForVendor(
                          !expandedDescriptionForVendor,
                        )
                      }
                      size="small"
                      variant="text"
                    >
                      {expandedDescriptionForVendor
                        ? t("serviceQuotation.collapseDescription")
                        : t("serviceQuotation.expandDescription")}
                    </Button>
                  </Box>
                  <OutlinedInput
                    fullWidth
                    inputProps={{
                      maxLength: VENDOR_DESCRIPTION_MAX_LENGTH,
                    }}
                    multiline
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setDescriptionForVendor(event.target.value);
                    }}
                    readOnly={!canEditVendorDescription}
                    rows={expandedDescriptionForVendor ? 15 : 5}
                    sx={{
                      backgroundColor: "white",
                      fontSize: "0.9em",
                      marginTop: "16px",
                    }}
                    value={descriptionForVendor}
                  />

                  <Box
                    sx={{
                      alignItems: "center",
                      display: "flex",
                      justifyContent: "space-between",
                      marginTop: 1,
                    }}
                  >
                    <Box>
                      <Typography
                        color={
                          descriptionForVendor.length ===
                          VENDOR_DESCRIPTION_MAX_LENGTH
                            ? "error"
                            : "text.secondary"
                        }
                        variant="caption"
                      >
                        {`${descriptionForVendor.length}/${VENDOR_DESCRIPTION_MAX_LENGTH}`}
                      </Typography>
                      {descriptionForVendor.length ===
                        VENDOR_DESCRIPTION_MAX_LENGTH && (
                        <Typography
                          color="error"
                          sx={{ display: "block", marginTop: 0.5 }}
                          variant="caption"
                        >
                          {t(
                            "serviceQuotation.vendorDescriptionMaxLengthReached",
                          )}
                        </Typography>
                      )}
                    </Box>

                    {!!canEditVendorDescription && (
                      <Button
                        disabled={
                          savingDescriptionForVendor ||
                          descriptionForVendor ===
                            (serviceQuotation?.description_for_vendor || "")
                        }
                        onClick={handleSaveDescriptionForVendor}
                        size="small"
                        variant="contained"
                      >
                        {savingDescriptionForVendor ? t("saving") : t("save")}
                      </Button>
                    )}
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        </Paper>
      </Container>
      <Container>
        <Paper elevation={1} sx={{ mb: 2, mt: 2 }}>
          <TabContext value={value}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <TabList
                aria-label="basic tabs example"
                onChange={handleChange}
                scrollButtons="auto"
                variant="scrollable"
              >
                <Tab
                  label={t("serviceNegotiation.negotiations")}
                  value={TAB_VALUES.negotiations}
                />
                <Tab
                  label={t("serviceNegotiation.services")}
                  value={TAB_VALUES.services}
                />
                <Tab
                  label={t("common.attachments")}
                  value={TAB_VALUES.attachments}
                />
                <Tab
                  label={t("serviceNegotiation.chat")}
                  value={TAB_VALUES.internalChat}
                />
                <Tab
                  label={t("serviceQuotation.plantMaintenanceOrder.title")}
                  value={TAB_VALUES.maintenanceOrder}
                />
                <Tab
                  label={t("serviceQuotation.moreInformation")}
                  value={TAB_VALUES.moreInformation}
                />
                <Tab
                  label={t("serviceNegotiation.audits")}
                  value={TAB_VALUES.audits}
                />
              </TabList>
            </Box>
            <TabPanel value={TAB_VALUES.negotiations}>
              <Container>
                {serviceQuotation &&
                  serviceNegotiations &&
                  PROPOSAL_EQUALIZATION_AVAILABLE_STATUS_NAMES.has(
                    serviceQuotation.status?.name ?? "",
                  ) && (
                    <ProposalEqualization
                      serviceNegotiations={serviceNegotiations}
                      serviceQuotation={serviceQuotation}
                    />
                  )}
              </Container>
            </TabPanel>
            <TabPanel value="1">
              <Container>
                {serviceNegotiations ? (
                  <ServiceNegotiationTable
                    canAward={canManageServiceQuotation}
                    serviceNegotiations={serviceNegotiations}
                  />
                ) : (
                  <Box
                    sx={{
                      alignItems: "center",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <Chip label={<span>{t("noInvitedVendor")}</span>} />
                  </Box>
                )}
              </Container>
            </TabPanel>
            <TabPanel value={TAB_VALUES.services}>
              <Container>
                {!!serviceQuotation && (
                  <ServiceTable rowsService={serviceQuotation.service_lines} />
                )}
              </Container>
            </TabPanel>
            <TabPanel value={TAB_VALUES.attachments}>
              <Container>
                {!!serviceQuotation?.id && (
                  <ListAttachmentServiceQuotation
                    focusScopeOfWorkTrigger={scopeOfWorkFocusTrigger}
                    isAssignedToCurrentUser={canManageServiceQuotation}
                    onDocumentsChanged={async () => {
                      await handleFetchServiceQuotation(serviceQuotation.id, {
                        showLoading: false,
                      });
                      setActionsRefreshTrigger((prev) => prev + 1);
                    }}
                    serviceQuotationId={serviceQuotation?.id}
                    serviceQuotationStatus={serviceQuotation?.status?.name}
                  />
                )}
              </Container>
            </TabPanel>
            <TabPanel value={TAB_VALUES.internalChat}>
              <Container>
                {!!serviceQuotation?.chat && (
                  <Box ref={chatSectionRef} tabIndex={-1}>
                    <ServiceNegotiationChats
                      onSelectionChange={(conversation) => {
                        const isNegotiation =
                          conversation.resource_type ===
                          "Vbuyer::ServiceNegotiation";
                        void navigate({
                          replace: true,
                          resetScroll: false,
                          search: (previous) => ({
                            ...previous,
                            service_negotiation_id: isNegotiation
                              ? conversation.resource_id
                              : undefined,
                            tab: isNegotiation
                              ? "negotiations"
                              : "internal_chat",
                          }),
                        });
                      }}
                      scrollToLatestRequest={scrollToLatestRequest}
                      selectedServiceNegotiationId={
                        selectedServiceNegotiationId
                      }
                      serviceQuotationId={serviceQuotation.id}
                    />
                  </Box>
                )}
              </Container>
            </TabPanel>
            <TabPanel value={TAB_VALUES.maintenanceOrder}>
              <Container>
                {!!serviceQuotation && (
                  <MaintenanceOrder
                    purchaseRequisition={serviceQuotation.pr_item}
                  />
                )}
              </Container>
            </TabPanel>
            <TabPanel value={TAB_VALUES.moreInformation}>
              <Container>
                {!!serviceQuotation && (
                  <ServiceBillingInformation
                    purchaseRequisition={serviceQuotation.pr_item}
                  />
                )}
              </Container>
            </TabPanel>
            <TabPanel value={TAB_VALUES.audits}>
              <Container>
                {!!serviceQuotation && (
                  <ServiceNegotiationAudits
                    serviceQuotation={serviceQuotation}
                  />
                )}
              </Container>
            </TabPanel>
          </TabContext>
        </Paper>
      </Container>
    </Box>
  );
};

export const Route = createFileRoute(
  "/_private/vbuyer/service_quotations/$serviceQuotationId/",
  // biome-ignore assist/source/useSortedKeys: ordering required for type inference, waiting a TanStack Biome plugin
)({
  validateSearch: searchSchema,
  beforeLoad({ context }: { context: RouteContext }) {
    if (context.auth.user?.roles_names.includes("Service Quotation - View")) {
      return;
    }

    throw new ForbiddenError();
  },
  component: ShowServiceQuotation,
});
